'use client'

import {
    getAccountingOnboardingStatus,
    OnboardingStatus,
    seedDefaultAccountingRules,
    seedDefaultChartOfAccounts,
    setDefaultCashAccount,
} from '@/lib/api/financial/financial-accounting-api'
import { getChartOfAccounts } from '@/lib/api/financial/financial-chart-of-accounts-api'
import { useEffect, useState } from 'react'
import {
    MdAccountBalance,
    MdCheckCircle,
    MdGavel,
    MdSavings,
} from 'react-icons/md'
import { toast } from 'react-toastify'

interface Account {
    id: number
    code: string
    title: string
    type: string
}

interface Props {
    onClose: () => void
    onCompleted: () => void
}

/**
 * Wizard de onboarding contabil. Dispara ao primeiro acesso de /financial/accounting
 * quando OnboardingStatus.completed === false. 3 etapas:
 *  1. Semear plano de contas SPED
 *  2. Semear regras contabeis padrao
 *  3. Selecionar conta caixa default
 *
 * Cada etapa eh idempotente: se ja foi feita, marcada como concluida e skipa.
 */
export default function AccountingOnboardingWizard({ onClose, onCompleted }: Props) {
    const [status, setStatus] = useState<OnboardingStatus | null>(null)
    const [accounts, setAccounts] = useState<Account[]>([])
    const [selectedCashId, setSelectedCashId] = useState<number | null>(null)
    const [step, setStep] = useState<1 | 2 | 3>(1)
    const [processing, setProcessing] = useState(false)

    useEffect(() => {
        load()
    }, [])

    const load = async () => {
        const res = await getAccountingOnboardingStatus()
        if (res.success && res.result) {
            setStatus(res.result)
            // Posicionar na primeira etapa nao concluida
            if (!res.result.hasChartOfAccounts) setStep(1)
            else if (!res.result.hasRules) setStep(2)
            else if (!res.result.hasDefaultCashAccount) setStep(3)
        }
    }

    const loadAccounts = async () => {
        const res = await getChartOfAccounts({ type: 'ASSET' })
        if (res.success && Array.isArray(res.result)) {
            // Filtra contas folha do grupo Disponivel (1.1.01.x)
            const cash = (res.result as any[]).filter(
                (a) => a.code.startsWith('1.1.01.') && a.code.split('.').length === 4,
            )
            setAccounts(cash)
            const caixaGeral = cash.find((a) => a.code === '1.1.01.001')
            if (caixaGeral) setSelectedCashId(caixaGeral.id)
        }
    }

    const handleSeedChart = async (replace = false) => {
        setProcessing(true)
        try {
            const res = await seedDefaultChartOfAccounts(replace)
            if (res.success && res.result) {
                if (res.result.alreadySeeded) {
                    // Mostra confirmacao de substituicao em vez de avancar.
                    setShowReplaceConfirm(true)
                    return
                }
                toast.success(
                    res.result.replacedCount
                        ? `Plano antigo substituido (${res.result.replacedCount} contas) e ${res.result.created} contas SPED criadas`
                        : `${res.result.created} contas criadas`,
                )
                await load()
                setStep(2)
            } else {
                toast.error(res.error || 'Erro ao semear plano de contas')
            }
        } finally {
            setProcessing(false)
        }
    }
    const [showReplaceConfirm, setShowReplaceConfirm] = useState(false)

    const handleSeedRules = async () => {
        setProcessing(true)
        try {
            const res = await seedDefaultAccountingRules()
            if (res.success && res.result) {
                toast.success(
                    res.result.alreadySeeded
                        ? 'Regras já existem'
                        : `${res.result.created} regras criadas`,
                )
                await load()
                setStep(3)
                await loadAccounts()
            } else {
                toast.error(res.error || 'Erro ao semear regras')
            }
        } finally {
            setProcessing(false)
        }
    }

    const handleSetCashAccount = async () => {
        if (!selectedCashId) {
            toast.warning('Selecione a conta caixa principal')
            return
        }
        setProcessing(true)
        try {
            const res = await setDefaultCashAccount(selectedCashId)
            if (res.success) {
                toast.success('Configuração concluída!')
                onCompleted()
            } else {
                toast.error(res.error || 'Erro ao definir conta caixa')
            }
        } finally {
            setProcessing(false)
        }
    }

    // Pula direto pra step 3 carregando accounts quando entrando ja com chart+rules
    useEffect(() => {
        if (status?.hasChartOfAccounts && status?.hasRules && step === 3) {
            loadAccounts()
        }
    }, [status?.hasChartOfAccounts, status?.hasRules, step])

    return (
        <dialog open className="modal modal-open">
            <div className="modal-box max-w-2xl">
                <h3 className="font-bold text-xl mb-1">
                    Configurar contabilidade
                </h3>
                <p className="text-sm text-base-content/60 mb-6">
                    Vamos configurar sua contabilidade em 3 passos rápidos. Tudo
                    pode ser ajustado depois.
                </p>

                {/* Steps indicator */}
                <ul className="steps w-full mb-6">
                    <li className={`step ${step >= 1 ? 'step-secondary' : ''}`}>
                        Plano de contas
                    </li>
                    <li className={`step ${step >= 2 ? 'step-secondary' : ''}`}>
                        Regras
                    </li>
                    <li className={`step ${step >= 3 ? 'step-secondary' : ''}`}>
                        Caixa
                    </li>
                </ul>

                {/* Step 1 */}
                {step === 1 && (
                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                <MdAccountBalance className="text-3xl text-secondary" />
                            </div>
                            <div>
                                <h4 className="font-bold text-base">
                                    Plano de Contas SPED
                                </h4>
                                <p className="text-sm text-base-content/70 mt-1">
                                    Vamos criar um plano de contas referencial
                                    SPED Contábil (RFB) com cerca de 200 contas
                                    organizadas em Ativo, Passivo, PL, Receitas
                                    e Despesas. Você pode editar, adicionar ou
                                    remover contas depois.
                                </p>
                            </div>
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={onClose}
                                disabled={processing}
                            >
                                Fechar
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleSeedChart(false)}
                                disabled={processing}
                            >
                                {processing ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    'Criar plano de contas'
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {showReplaceConfirm && (
                    <dialog open className="modal modal-open">
                        <div className="modal-box max-w-md">
                            <h3 className="font-bold text-lg mb-2">
                                Plano de contas existente
                            </h3>
                            <p className="text-sm text-base-content/70 mb-3">
                                Esta empresa ja tem {status?.chartOfAccountsCount}{' '}
                                contas cadastradas, mas com codigos diferentes do
                                padrao SPED. As regras automaticas e relatorios so
                                funcionam com o plano SPED.
                            </p>
                            <div className="alert alert-warning text-xs py-2">
                                Ao substituir, o plano antigo sera marcado como
                                excluido (soft-delete), as regras serao desativadas
                                e a conta caixa default sera limpa. Lancamentos
                                contabeis existentes permanecem no historico mas
                                ficam associados ao plano antigo.
                            </div>
                            <div className="modal-action">
                                <button
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => setShowReplaceConfirm(false)}
                                    disabled={processing}
                                >
                                    Cancelar
                                </button>
                                <button
                                    className="btn btn-warning btn-sm"
                                    onClick={() => {
                                        setShowReplaceConfirm(false)
                                        handleSeedChart(true)
                                    }}
                                    disabled={processing}
                                >
                                    Substituir e usar SPED
                                </button>
                            </div>
                        </div>
                    </dialog>
                )}

                {/* Step 2 */}
                {step === 2 && (
                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                <MdGavel className="text-3xl text-secondary" />
                            </div>
                            <div>
                                <h4 className="font-bold text-base">
                                    Regras de Classificação Automática
                                </h4>
                                <p className="text-sm text-base-content/70 mt-1">
                                    Criaremos cerca de 25 regras padrão que
                                    classificam lançamentos contábeis
                                    automaticamente a partir do método de
                                    pagamento (PIX → Caixa/Receita; Boleto →
                                    Despesa/Banco; etc). Você pode customizar
                                    depois.
                                </p>
                            </div>
                        </div>
                        <div className="alert alert-success text-sm py-2">
                            <MdCheckCircle />
                            Plano de contas criado ({status?.chartOfAccountsCount}{' '}
                            contas)
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setStep(1)}
                                disabled={processing}
                            >
                                Voltar
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleSeedRules}
                                disabled={processing}
                            >
                                {processing ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    'Criar regras automáticas'
                                )}
                            </button>
                        </div>
                    </div>
                )}

                {/* Step 3 */}
                {step === 3 && (
                    <div className="space-y-4">
                        <div className="flex items-start gap-4">
                            <div className="w-14 h-14 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                                <MdSavings className="text-3xl text-secondary" />
                            </div>
                            <div>
                                <h4 className="font-bold text-base">
                                    Conta Caixa Principal
                                </h4>
                                <p className="text-sm text-base-content/70 mt-1">
                                    Selecione a conta que representa o caixa
                                    principal da sua empresa. As regras
                                    automáticas usarão essa conta como destino
                                    de entradas/saídas em dinheiro quando não
                                    houver conta bancária específica.
                                </p>
                            </div>
                        </div>
                        <div className="alert alert-success text-sm py-2">
                            <MdCheckCircle />
                            Regras criadas ({status?.rulesCount} regras)
                        </div>
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-medium">
                                    Conta caixa default *
                                </span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={selectedCashId ?? ''}
                                onChange={(e) =>
                                    setSelectedCashId(
                                        e.target.value
                                            ? Number(e.target.value)
                                            : null,
                                    )
                                }
                            >
                                <option value="">Selecione...</option>
                                {accounts.map((a) => (
                                    <option key={a.id} value={a.id}>
                                        {a.code} — {a.title}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="modal-action">
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setStep(2)}
                                disabled={processing}
                            >
                                Voltar
                            </button>
                            <button
                                className="btn btn-secondary btn-sm"
                                onClick={handleSetCashAccount}
                                disabled={processing || !selectedCashId}
                            >
                                {processing ? (
                                    <span className="loading loading-spinner loading-xs" />
                                ) : (
                                    'Concluir configuração'
                                )}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </dialog>
    )
}
